import type { AlertColor } from "@mui/material/Alert/Alert";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Route, Routes } from "react-router-dom";

import useApi from "../../api/apiConfig";
import { PREDEFINED_BANK_TRANSACTION_FILTERS } from "../../status";
import {
  type BackendFilters,
  createDefaultBackendFilters,
} from "../../utils/backendFilters";
import useDevMode from "../../utils/devmode";
import { getBankTransactionById } from "./api";
import {
  markBankTransactionListsStale,
  patchBankTransaction,
  removeBankTransaction,
  useBankTransactions,
  useFindMatches,
  useReconcileBankTransactions,
  useUnmatchBankTransaction,
  useUpdateBankTransaction,
  useUploadBankTransactions,
} from "./hooks";
import { expectedFromUpdate } from "./pendingMutations";
import ReconciliationListView from "./ReconciliationListView";
import SuggestedMatchesModal from "./SuggestedMatchesModal";
import TransactionConfirmationModal from "./TransactionConfirmationModal";
import { type SettleOutcome, usePendingMutations } from "./usePendingMutations";

const mapFilterToTab = (
  filter: PREDEFINED_BANK_TRANSACTION_FILTERS,
): string => {
  switch (filter) {
    case PREDEFINED_BANK_TRANSACTION_FILTERS.REVIEW:
      return "REVIEW";
    case PREDEFINED_BANK_TRANSACTION_FILTERS.PENDING:
      return "PENDING";
    case PREDEFINED_BANK_TRANSACTION_FILTERS.ALL:
      return "ALL";
    default:
      return "ALL";
  }
};

// Mirrors backend bank_transaction_review_filter / PENDING filter so we can
// decide whether a settled row still belongs on the active tab.
const matchesFilter = (
  tx: BankTransactionMessage,
  filter: PREDEFINED_BANK_TRANSACTION_FILTERS,
): boolean => {
  switch (filter) {
    case PREDEFINED_BANK_TRANSACTION_FILTERS.REVIEW:
      return (
        !tx.transactionType ||
        ((tx.transactionType as unknown as string) === "INSURANCE" &&
          !tx.status)
      );
    case PREDEFINED_BANK_TRANSACTION_FILTERS.PENDING:
      return tx.status === "MISSING";
    default:
      return true;
  }
};

interface ReconciliationDashboardProps {
  selectedPractices: PracticeMessage[];
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}

export default function ReconciliationDashboard({
  selectedPractices,
  updateSnackBar,
}: ReconciliationDashboardProps) {
  const { getClient } = useApi();
  const queryClient = useQueryClient();
  const devMode = useDevMode();
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [uploadedTransactions, setUploadedTransactions] = useState<
    BankTransactionMessage[]
  >([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState<string>("");
  const [csvFileId, setCsvFileId] = useState<string | undefined>(undefined);

  const [suggestedMatchesModalVisible, setSuggestedMatchesModalVisible] =
    useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<BankTransactionMessage | null>(null);
  const [isFetchingMatches, setIsFetchingMatches] = useState(false);
  const [selectedFilter, setSelectedFilter] =
    useState<PREDEFINED_BANK_TRANSACTION_FILTERS>(
      PREDEFINED_BANK_TRANSACTION_FILTERS.REVIEW,
    );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>("bank_date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [backendFilters, setBackendFilters] = useState<BackendFilters>(
    createDefaultBackendFilters,
  );

  const practiceIds = useMemo(
    () => selectedPractices.map((p) => p.wieldyId),
    [selectedPractices],
  );
  const searchParams = useMemo(
    () => ({
      practiceIds,
      tab: mapFilterToTab(selectedFilter),
      page,
      pageSize,
      sortField,
      sortDirection,
      filters: backendFilters,
      devMode,
    }),
    [
      practiceIds,
      selectedFilter,
      page,
      pageSize,
      sortField,
      sortDirection,
      backendFilters,
      devMode,
    ],
  );
  const { data, isFetching, isError, error, errorUpdatedAt, refetch } =
    useBankTransactions(searchParams, practiceIds.length > 0);
  const transactions = useMemo(
    () => (data?.data ?? []) as BankTransactionMessage[],
    [data],
  );

  const { mutateAsync: updateAsync } = useUpdateBankTransaction();
  const { mutateAsync: unmatchAsync } = useUnmatchBankTransaction();
  const { mutateAsync: findMatchesAsync } = useFindMatches();
  const { mutateAsync: uploadAsync } = useUploadBankTransactions();
  const { mutateAsync: reconcileAsync } = useReconcileBankTransactions();

  const handleSettled = useCallback(
    (wieldyId: string, outcome: SettleOutcome) => {
      if (outcome.kind === "timeout") {
        updateSnackBar({
          severity: "warning",
          message:
            "Still syncing — refresh if this transaction doesn't update shortly.",
        });
        return;
      }
      markBankTransactionListsStale(queryClient);
      if (outcome.kind === "gone") {
        removeBankTransaction(queryClient, wieldyId);
        return;
      }
      const { server } = outcome;
      if (!matchesFilter(server, selectedFilter)) {
        removeBankTransaction(queryClient, wieldyId);
      } else {
        patchBankTransaction(queryClient, {
          wieldyId: server.wieldyId,
          practiceId: server.practiceId,
          transactionType: server.transactionType,
          status: server.status,
          paymentKey: server.paymentKey,
          payerPaymentId: server.payerPaymentId,
          matchedAt: server.matchedAt,
          reportedMissingAt: server.reportedMissingAt,
        });
      }
    },
    [selectedFilter, updateSnackBar, queryClient],
  );

  const fetchTransactionById = useCallback(
    async (wieldyId: string, signal?: AbortSignal) =>
      getBankTransactionById(await getClient(), wieldyId, signal),
    [getClient],
  );

  const {
    pending,
    register: registerPending,
    clearAll: clearPending,
  } = usePendingMutations({
    fetchTransactionById,
    onSettled: handleSettled,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: errorUpdatedAt marks each new failure; adding isError, error or updateSnackBar would re-show the snackbar
  useEffect(() => {
    if (!isError) return;
    updateSnackBar({
      severity: "error",
      message: "Failed to load reconciliations. Please try again.",
    });
    // biome-ignore lint/suspicious/noConsole: intentional browser console output
    console.error("Error loading reconciliations:", error);
  }, [errorUpdatedAt]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: searchParams is the trigger, a new query means any pending row is stale
  useEffect(() => {
    clearPending();
  }, [searchParams, clearPending]);

  const uploadBankTransactions = useCallback(
    async (practiceId: string, file: File) => {
      try {
        const response = await uploadAsync({ practiceId, file });

        setUploadedTransactions(response.transactions);
        setCsvFileId(response.csvFileId);
        setSelectedPracticeId(practiceId);
        setConfirmModalVisible(true);
        updateSnackBar({
          severity: "success",
          message: `Successfully parsed ${response.transactions.length} transactions. Please review and confirm.`,
        });
      } catch (err) {
        updateSnackBar({
          severity: "error",
          message: "Failed to upload transactions. Please try again.",
        });
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Upload bank transactions failed:", err);
      }
    },
    [uploadAsync, updateSnackBar],
  );

  const reconcileTransactions = useCallback(
    async (reviewed: BankTransactionMessage[]) => {
      try {
        const response = await reconcileAsync({
          practiceId: selectedPracticeId,
          transactions: reviewed,
          csvFileId,
        });

        const { total, insuranceCount, autoMatchedCount } = response.summary;
        updateSnackBar({
          severity: "success",
          message: `Reconciled ${total} transactions (${insuranceCount} insurance, ${autoMatchedCount} auto-matched)`,
        });
      } catch (err) {
        updateSnackBar({
          severity: "error",
          message: "Failed to reconcile transactions. Please try again.",
        });
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Reconcile bank transactions failed:", err);
      }
    },
    [reconcileAsync, updateSnackBar, selectedPracticeId, csvFileId],
  );

  const updateTransaction = useCallback(
    async (transactionUpdate: BankTransactionUpdateMessage) => {
      try {
        await updateAsync({ update: transactionUpdate, optimistic: true });
        updateSnackBar({
          severity: "success",
          message: "Transaction updated successfully",
        });
        registerPending(
          transactionUpdate.wieldyId,
          expectedFromUpdate(transactionUpdate),
        );
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Update transaction failed:", err);
        updateSnackBar({
          severity: "error",
          message: "Failed to update transaction",
        });

        throw err;
      }
    },
    [updateAsync, updateSnackBar, registerPending],
  );

  const unmatchTransaction = useCallback(
    async (wieldyId: string) => {
      try {
        const transaction = transactions.find((t) => t.wieldyId === wieldyId);
        if (!transaction) {
          throw new Error("Transaction not found");
        }
        await unmatchAsync({ wieldyId, practiceId: transaction.practiceId });

        updateSnackBar({
          severity: "success",
          message: "Transaction reset successfully",
        });
        registerPending(
          wieldyId,
          expectedFromUpdate({
            wieldyId,
            practiceId: transaction.practiceId,
            paymentKey: null,
            status: null,
          }),
        );
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Reset transaction failed:", err);
        updateSnackBar({
          severity: "error",
          message: "Failed to reset transaction. Please try again.",
        });
      }
    },
    [unmatchAsync, updateSnackBar, transactions, registerPending],
  );

  const handleFilterChange = useCallback((filters: BackendFilters) => {
    setBackendFilters(filters);
    setPage(1);
  }, []);

  const handleSortChange = useCallback(
    (field: string, direction: "asc" | "desc") => {
      setSortField(field);
      setSortDirection(direction);
      setPage(1);
    },
    [],
  );

  const handlePageChange = useCallback((nextPage: number, size: number) => {
    setPage(nextPage);
    setPageSize(size);
  }, []);

  const handleFindMatches = useCallback(
    async (transaction: BankTransactionMessage) => {
      setSelectedTransaction(transaction);
      setSuggestedMatchesModalVisible(true);
      setIsFetchingMatches(true);

      try {
        const response = await findMatchesAsync(transaction.wieldyId);

        if (response.autoMatched) {
          // Transaction was auto-matched, keep modal open and show success
          setSelectedTransaction({
            ...transaction,
            paymentKey: response.paymentKey,
            payerPaymentId: response.payerPaymentId,
            autoMatched: true,
          });
          const optimisticUpdate: BankTransactionUpdateMessage = {
            wieldyId: transaction.wieldyId,
            practiceId: transaction.practiceId,
            paymentKey: response.paymentKey,
            status: "AUTO_MATCHED",
          };
          patchBankTransaction(queryClient, optimisticUpdate);
          registerPending(
            transaction.wieldyId,
            expectedFromUpdate(optimisticUpdate),
          );
        } else if (
          response.suggestedMatches &&
          response.suggestedMatches.length > 0
        ) {
          setSelectedTransaction({
            ...transaction,
            suggestedMatches: response.suggestedMatches,
          });
        } else {
          // No matches found - keep modal open, user can mark as posted outside or missing
          setSelectedTransaction({
            ...transaction,
            suggestedMatches: [],
            noMatchesFound: true,
          });
        }
      } catch (err) {
        setSuggestedMatchesModalVisible(false);
        setSelectedTransaction(null);
        updateSnackBar({
          severity: "error",
          message: "Failed to find matches. Please try again.",
        });
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Find matches failed:", err);
      } finally {
        setIsFetchingMatches(false);
      }
    },
    [findMatchesAsync, queryClient, updateSnackBar, registerPending],
  );

  const handleAcceptMatch = useCallback(
    async (transactionId: string, paymentKey: string) => {
      try {
        const transactionUpdate: BankTransactionUpdateMessage = {
          wieldyId: transactionId,
          practiceId:
            selectedPracticeId || (selectedTransaction?.practiceId as string),
          paymentKey,
        };

        await updateAsync({
          update: transactionUpdate,
          patch: { ...transactionUpdate, status: "MANUALLY_MATCHED" },
        });

        updateSnackBar({
          severity: "success",
          message: "Match accepted successfully",
        });
        registerPending(transactionId, expectedFromUpdate(transactionUpdate));
      } catch (err) {
        updateSnackBar({
          severity: "error",
          message: "Failed to accept match",
        });
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Failed to accept match:", err);
      }
    },
    [
      updateAsync,
      updateSnackBar,
      selectedPracticeId,
      selectedTransaction,
      registerPending,
    ],
  );

  const prevTransactionCountRef = useRef(transactions.length);
  // biome-ignore lint/correctness/useExhaustiveDependencies: adding page or refetch would change when this runs
  useEffect(() => {
    const prev = prevTransactionCountRef.current;
    const curr = transactions.length;
    prevTransactionCountRef.current = curr;

    const justEmptied =
      prev > 0 && curr === 0 && (data?.total ?? 0) > 0 && !isFetching;
    if (!justEmptied) return;

    if (page > 1) {
      setPage(1);
      return;
    }
    refetch();
  }, [transactions.length, data?.total, isFetching]);

  const pendingIds = React.useMemo(() => new Set(pending.keys()), [pending]);

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <ReconciliationListView
              transactions={transactions}
              isLoading={false}
              transactionsLoading={isFetching}
              selectedPractices={selectedPractices}
              uploadBankTransactions={uploadBankTransactions}
              updateTransaction={updateTransaction}
              onFindMatches={handleFindMatches}
              onUnmatchTransaction={unmatchTransaction}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              onFilterChange={handleFilterChange}
              onSortChange={handleSortChange}
              page={page}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              rowCount={data?.total ?? 0}
              pendingIds={pendingIds}
            />
          }
        />
      </Routes>

      <TransactionConfirmationModal
        open={confirmModalVisible}
        onClose={() => setConfirmModalVisible(false)}
        transactions={uploadedTransactions}
        onConfirm={reconcileTransactions}
      />

      {selectedTransaction && (
        <SuggestedMatchesModal
          open={suggestedMatchesModalVisible}
          onClose={() => {
            setSuggestedMatchesModalVisible(false);
            setSelectedTransaction(null);
            setIsFetchingMatches(false);
          }}
          transaction={selectedTransaction}
          onAcceptMatch={handleAcceptMatch}
          onUpdateTransaction={updateTransaction}
          isLoading={isFetchingMatches}
        />
      )}
    </>
  );
}
